import React, { useState } from 'react';
import { MOOD_OPTIONS, PIN_STYLES } from '../constants';

const FilterBar = ({
    selectedMoods,
    setSelectedMoods,
    selectedStyles,
    setSelectedStyles,
    isOpen,
    setIsOpen
}) => {
    const [activeTab, setActiveTab] = useState('moods'); // 'moods' or 'styles'

    const toggleMood = (mood) => {
        if (selectedMoods.includes(mood)) {
            setSelectedMoods(selectedMoods.filter(m => m !== mood));
        } else {
            setSelectedMoods([...selectedMoods, mood]);
        }
    };

    const toggleStyle = (styleId) => {
        if (selectedStyles.includes(styleId)) {
            setSelectedStyles(selectedStyles.filter(s => s !== styleId));
        } else {
            setSelectedStyles([...selectedStyles, styleId]);
        }
    };

    const clearFilters = () => {
        setSelectedMoods([]);
        setSelectedStyles([]);
    };

    const hasFilters = selectedMoods.length > 0 || selectedStyles.length > 0;

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className={`
          group flex items-center bg-white/95 hover:bg-white text-gray-900 
          dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-white
          rounded-xl shadow-2xl hover:shadow-white/20 transition-all duration-300 
          transform hover:scale-105 backdrop-blur-sm border border-white/20 dark:border-gray-700
          overflow-hidden
          ${hasFilters ? 'ring-2 ring-indigo-500' : ''}
        `}
                title="Filter marks"
            >
                <div className="p-2.5 relative">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    {hasFilters && (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-indigo-500 rounded-full border border-white"></span>
                    )}
                </div>
                <span className="max-w-0 group-hover:max-w-xs pr-0 group-hover:pr-3 pl-0 group-hover:pl-1 opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap font-medium text-sm">
                    Filters {hasFilters && `(${selectedMoods.length + selectedStyles.length})`}
                </span>
            </button>
        );
    }

    return (
        <div className="absolute top-full right-0 mt-2 w-72 md:w-80 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 dark:border-gray-700 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-[2000]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-gray-800/50 dark:to-gray-900/50">
                <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    Filter Marks
                </h3>
                <div className="flex items-center gap-2">
                    {hasFilters && (
                        <button
                            onClick={clearFilters}
                            className="text-xs font-medium text-red-500 hover:text-red-600 transition-colors px-2 py-1 rounded bg-red-50 hover:bg-red-100"
                        >
                            Clear
                        </button>
                    )}
                    <button
                        onClick={() => setIsOpen(false)}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-100 dark:border-gray-700">
                <button
                    onClick={() => setActiveTab('moods')}
                    className={`flex-1 py-3 text-sm font-medium transition-colors relative ${activeTab === 'moods'
                        ? 'text-indigo-600 bg-indigo-50/30 dark:text-indigo-400 dark:bg-indigo-900/30'
                        : 'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700'
                        }`}
                >
                    Moods
                    {selectedMoods.length > 0 && (
                        <span className="ml-1.5 bg-indigo-100 text-indigo-600 text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center justify-center min-w-[1.25rem]">
                            {selectedMoods.length}
                        </span>
                    )}
                    {activeTab === 'moods' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 rounded-t-full mx-4"></div>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('styles')}
                    className={`flex-1 py-3 text-sm font-medium transition-colors relative ${activeTab === 'styles'
                        ? 'text-purple-600 bg-purple-50/30 dark:text-purple-400 dark:bg-purple-900/30'
                        : 'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700'
                        }`}
                >
                    Styles
                    {selectedStyles.length > 0 && (
                        <span className="ml-1.5 bg-purple-100 text-purple-600 text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center justify-center min-w-[1.25rem]">
                            {selectedStyles.length}
                        </span>
                    )}
                    {activeTab === 'styles' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500 rounded-t-full mx-4"></div>
                    )}
                </button>
            </div>

            {/* Content */}
            <div className="p-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
                {activeTab === 'moods' ? (
                    <div className="flex flex-wrap gap-2">
                        {MOOD_OPTIONS.map((mood) => (
                            <button
                                key={mood}
                                onClick={() => toggleMood(mood)}
                                className={`
                  px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 border
                  ${selectedMoods.includes(mood)
                                        ? 'bg-indigo-500 border-indigo-500 text-white shadow-md transform scale-105'
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-indigo-200 hover:bg-indigo-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-indigo-900/50 dark:hover:border-indigo-700'
                                    }
                `}
                            >
                                {mood}
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-4 gap-2">
                        {PIN_STYLES.map((style) => (
                            <button
                                key={style.id}
                                onClick={() => toggleStyle(style.id)}
                                className={`
                  flex flex-col items-center justify-center p-2 rounded-xl transition-all duration-200 border
                  ${selectedStyles.includes(style.id)
                                        ? `bg-gradient-to-br ${style.color} text-white border-transparent shadow-md transform scale-105`
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-purple-200 hover:bg-purple-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-purple-900/50 dark:hover:border-purple-700'
                                    }
                `}
                            >
                                <span className="text-xl mb-1">{style.icon}</span>
                                <span className={`text-[10px] font-medium truncate w-full text-center ${selectedStyles.includes(style.id) ? 'text-white/90' : 'text-gray-500'
                                    }`}>
                                    {style.name}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer Info */}
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700 text-[10px] text-gray-500 dark:text-gray-400 text-center">
                Showing matches for
                <span className="font-medium text-gray-700 dark:text-gray-300"> ANY </span>
                selected filters
            </div>
        </div>
    );
};

export default FilterBar;
